import type { MatchPhase, ProjectState } from "@livcg/schema";
import { Pause, Play, PowerOff } from "lucide-react";
import { useLiveClock } from "../../shared/liveClock.js";
import { sendRaw } from "../../shared/sendRaw.js";
import { cn, HoldButton } from "../../ui/index.js";
import { PHASE_LABEL, PHASES } from "../phases.js";

const pad = (n: number) => String(n).padStart(2, "0");
const BIG_BTN =
  "h-12 rounded-lg border border-border bg-surface-elevated px-3 text-[15px] font-bold text-ink hover:bg-surface-hover";

/** Clock, score, phase and ALL OFF. These act directly — they steer the match,
 *  they are not cued graphic content. */
export function TopBar({
  slug,
  state,
  onAllOff,
}: {
  slug: string;
  state: ProjectState;
  onAllOff: () => void;
}) {
  const clock = useLiveClock(slug);
  const adjust = (deltaSeconds: number) => void sendRaw("clock.adjust", { slug, deltaSeconds });
  const home = state.teams.home;
  const away = state.teams.away;
  return (
    <div className="flex items-center gap-4 rounded-xl border border-border bg-surface px-4 py-2.5">
      <span className="w-[150px] font-display text-[44px] font-black leading-none tabular-nums text-ink">
        {pad(clock.minute)}:{pad(clock.second)}
      </span>
      <button
        type="button"
        onClick={() => void sendRaw("clock.toggle", { slug })}
        className={cn(
          "flex h-12 items-center gap-2 rounded-lg px-5 text-[16px] font-black uppercase tracking-wide",
          clock.running ? "bg-card-amber text-card-amber-ink" : "bg-ready-strong text-white",
        )}
        title="Space"
      >
        {clock.running ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
        {clock.running ? "Pauza" : "Start"}
      </button>
      <div className="flex gap-1.5">
        <button type="button" className={BIG_BTN} onClick={() => adjust(-10)}>
          −10s
        </button>
        <button type="button" className={BIG_BTN} onClick={() => adjust(-1)}>
          −1s
        </button>
        <button type="button" className={BIG_BTN} onClick={() => adjust(1)}>
          +1s
        </button>
        <button type="button" className={BIG_BTN} onClick={() => adjust(10)}>
          +10s
        </button>
      </div>
      <select
        value={state.match.phase}
        onChange={(e) =>
          void sendRaw("clock.setPhase", { slug, phase: e.target.value as MatchPhase })
        }
        className="h-12 rounded-lg border border-border bg-surface-elevated px-3 text-[15px] font-semibold text-ink"
      >
        {PHASES.map((p) => (
          <option key={p} value={p}>
            {PHASE_LABEL[p]}
          </option>
        ))}
      </select>
      <div className="ml-auto flex items-center gap-3 font-display text-[30px] font-black text-ink">
        <span className="text-[18px] font-bold text-ink-muted">
          {home?.code || home?.shortName}
        </span>
        <span className="tabular-nums">
          {state.match.homeScore} : {state.match.awayScore}
        </span>
        <span className="text-[18px] font-bold text-ink-muted">
          {away?.code || away?.shortName}
        </span>
      </div>
      <HoldButton
        onConfirm={onAllOff}
        title="Bosib turing — barcha overlay va VAR o'chadi (Shift+Esc)"
        className="flex h-12 items-center gap-2 rounded-lg border border-live bg-live-soft px-4 text-[15px] font-black uppercase text-live"
      >
        <PowerOff className="h-5 w-5" />
        All off
      </HoldButton>
    </div>
  );
}
